import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
  Button,
  Empty,
  Input,
  Modal,
  Pagination,
  Progress,
  Result,
  Spin,
  Tag,
  message
} from 'antd'
import {
  CalendarOutlined,
  DollarOutlined,
  ReloadOutlined,
  SearchOutlined,
  UserOutlined,
  WarningOutlined
} from '@ant-design/icons'
import { useSearchParams } from 'react-router-dom'

import { useAuth } from '../hooks/AuthContext'
import { useMembership } from '../hooks/useMembership'
import { usePermission } from '../hooks/usePermission'
import { getApiErrorMessage } from '../utils/api'
import {
  listInstallmentPlans,
  cancelInstallmentPlan
} from '../api/installment'
import ResponsiveSelect from '../components/common/ResponsiveSelect'
import InstallmentPaymentModal from '../components/installment/InstallmentPaymentModal'
import InstallmentDetailModal from '../components/installment/InstallmentDetailModal'
import {
  INSTALLMENT_FREQUENCY_LABEL as FREQUENCY_LABEL,
  installmentMoney as money
} from '../components/installment/installmentShared'

/**
 * 分期收款（店內自行分期）。
 *
 * 這一頁是店家每天真正會點開的工作檯：誰還欠、哪一期逾期了、今天收了誰的錢。
 * 應收帳款的「數字」在營收報表，這裡管的是「動作」。
 *
 * 收款一定要填收款日期 —— 現金流是以 InstallmentPayment.paidAt 計入的，
 * 沿用結帳日會把客人三個月後繳的錢算回結帳當天。
 */

const PageWrapper = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 20px 24px;

  @media (max-width: 768px) {
    padding: 12px;
  }
`

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
`

const SummaryCard = styled.div`
  border: 1px solid #e5e5e7;
  border-radius: 12px;
  padding: 16px;
  background: ${(p) => (p.$warn ? '#fff7f5' : '#fafafa')};
  border-color: ${(p) => (p.$warn ? '#f4c7bd' : '#e5e5e7')};

  .label {
    font-size: 13px;
    color: #86868b;
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
  }

  .value {
    font-size: 24px;
    font-weight: 600;
    color: ${(p) => (p.$warn ? '#c0392b' : '#1d1d1f')};
  }

  .hint {
    font-size: 12px;
    color: #a0a0a5;
    margin-top: 4px;
  }
`

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 16px;
`

const PlanCard = styled.div`
  border: 1px solid ${(p) => (p.$overdue ? '#f4c7bd' : '#e5e5e7')};
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  background: #fff;

  .head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 12px;
  }

  .name {
    font-size: 16px;
    font-weight: 600;
    color: #1d1d1f;
  }

  .meta {
    font-size: 12px;
    color: #86868b;
    margin-top: 2px;
  }

  .amounts {
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
    font-size: 13px;
    color: #57606a;
    margin: 10px 0;

    strong {
      color: #1d1d1f;
      font-size: 15px;
    }
  }

  .actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
`

const InstallmentsPage = () => {
  const { user } = useAuth()
  const { hasFeature, permissionsLoaded } = useMembership()
  const { can, canAny } = usePermission()

  const businessId = user?.businessId
  const featureEnabled = hasFeature('installment')
  const canView = canAny(['installment:view', 'installment:manage'])
  const canManage = can('installment:manage')

  const [loading, setLoading] = useState(false)
  const [plans, setPlans] = useState([])
  const [summary, setSummary] = useState({
    outstanding: 0,
    overdue: 0,
    activeCount: 0
  })
  const [status, setStatus] = useState('active')
  // 從顧客資料頁等處帶 ?keyword= 進來時，直接篩好那位客人
  const [searchParams] = useSearchParams()
  const initialKeyword = searchParams.get('keyword') || ''
  const [keyword, setKeyword] = useState(initialKeyword)
  const [searchInput, setSearchInput] = useState(initialKeyword)
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 })

  // 收款 / 明細 Modal（與顧客資料頁、結帳頁共用同一組元件）
  const [payModalPlan, setPayModalPlan] = useState(null)
  const [detailPlan, setDetailPlan] = useState(null)

  const fetchPlans = useCallback(
    async (page = 1, pageSize = 10) => {
      if (!businessId || !featureEnabled || !canView) return
      setLoading(true)
      try {
        const res = await listInstallmentPlans(businessId, {
          status: status === 'all' ? undefined : status,
          keyword: keyword || undefined,
          page,
          pageSize
        })
        setPlans(res?.data?.data || [])
        const meta = res?.data?.meta || {}
        setSummary(meta.summary || { outstanding: 0, overdue: 0, activeCount: 0 })
        setPagination({
          current: meta.page || page,
          pageSize: meta.pageSize || pageSize,
          total: meta.total || 0
        })
      } catch (err) {
        message.error(getApiErrorMessage(err, '取得分期計畫失敗'))
      } finally {
        setLoading(false)
      }
    },
    [businessId, featureEnabled, canView, status, keyword]
  )

  useEffect(() => {
    fetchPlans(1, pagination.pageSize)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, keyword, businessId])

  const handleCancelPlan = (plan) => {
    let reason = ''
    Modal.confirm({
      title: `取消 ${plan.customerName} 的分期計畫？`,
      content: (
        <div>
          <p style={{ fontSize: 12, color: '#86868b', marginBottom: 8 }}>
            已收的款項會保留在紀錄裡（那些錢是真的收過的），只是不再追收剩下的{' '}
            {money(plan.outstanding)}。
          </p>
          <Input.TextArea
            rows={2}
            placeholder='取消原因（選填）'
            onChange={(e) => {
              reason = e.target.value
            }}
          />
        </div>
      ),
      okText: '取消計畫',
      cancelText: '返回',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await cancelInstallmentPlan(businessId, plan._id, reason)
          message.success('已取消這筆分期計畫')
          fetchPlans(pagination.current, pagination.pageSize)
        } catch (err) {
          message.error(getApiErrorMessage(err, '取消失敗'))
        }
      }
    })
  }

  const statusOptions = useMemo(
    () => [
      { value: 'active', label: '進行中' },
      { value: 'overdue', label: '已逾期' },
      { value: 'completed', label: '已結清' },
      { value: 'canceled', label: '已取消' },
      { value: 'all', label: '全部' }
    ],
    []
  )

  if (!permissionsLoaded) {
    return (
      <PageWrapper>
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin />
        </div>
      </PageWrapper>
    )
  }

  if (!featureEnabled) {
    return (
      <Result
        status='info'
        title='分期收款是高階方案的功能'
        subTitle='升級後可以讓客人分期繳款，並自動追蹤每一期的到期與逾期狀況。'
      />
    )
  }

  if (!canView) {
    return <Result status='403' title='沒有權限' subTitle='請聯繫店主開啟分期收款權限。' />
  }

  return (
    <PageWrapper>
      <SummaryRow>
        <SummaryCard>
          <div className='label'>
            <DollarOutlined /> 未收餘額
          </div>
          <div className='value'>{money(summary.outstanding)}</div>
          <div className='hint'>客人還沒繳的分期款總額</div>
        </SummaryCard>
        <SummaryCard $warn={summary.overdue > 0}>
          <div className='label'>
            <WarningOutlined /> 逾期金額
          </div>
          <div className='value'>{money(summary.overdue)}</div>
          <div className='hint'>已過到期日（含寬限期）仍未收</div>
        </SummaryCard>
        <SummaryCard>
          <div className='label'>
            <CalendarOutlined /> 進行中計畫
          </div>
          <div className='value'>{summary.activeCount}</div>
          <div className='hint'>還沒收完的分期單數</div>
        </SummaryCard>
      </SummaryRow>

      <Filters>
        <ResponsiveSelect
          value={status}
          onChange={setStatus}
          options={statusOptions}
          style={{ width: 140 }}
        />
        <Input
          placeholder='客人姓名 / 電話 / 訂單編號'
          value={searchInput}
          onChange={(e) => {
            const next = e.target.value
            setSearchInput(next)
            // 清空時直接還原列表，不用再按一次查詢
            if (!next) setKeyword('')
          }}
          onPressEnter={() => setKeyword(searchInput.trim())}
          style={{ width: 240 }}
          allowClear
        />
        <Button
          type='primary'
          icon={<SearchOutlined />}
          onClick={() => setKeyword(searchInput.trim())}
        >
          查詢
        </Button>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => fetchPlans(pagination.current, pagination.pageSize)}
        />
      </Filters>

      <Spin spinning={loading}>
        {plans.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description='沒有符合條件的分期計畫'
          />
        ) : (
          plans.map((plan) => {
            const total = plan.totalWithFee || 0
            const percent =
              total > 0 ? Math.round(((plan.paidAmount || 0) / total) * 100) : 0
            return (
              <PlanCard key={plan._id} $overdue={plan.isOverdue}>
                <div className='head'>
                  <div>
                    <div className='name'>
                      <UserOutlined style={{ color: '#d5728a', marginRight: 6 }} />
                      {plan.customerName || '—'}
                      {plan.isOverdue && (
                        <Tag color='red' style={{ marginLeft: 8 }}>
                          逾期 {money(plan.overdueAmount)}
                        </Tag>
                      )}
                      {plan.status === 'completed' && (
                        <Tag color='green' style={{ marginLeft: 8 }}>
                          已結清
                        </Tag>
                      )}
                      {plan.status === 'canceled' && (
                        <Tag style={{ marginLeft: 8 }}>已取消</Tag>
                      )}
                    </div>
                    <div className='meta'>
                      {plan.customerPhone} · {plan.orderCode} · 分 {plan.periods} 期 ·{' '}
                      {FREQUENCY_LABEL[plan.frequency] || ''}
                      {plan.downPayment > 0 && (
                        <> · 頭期款 {money(plan.downPayment)}</>
                      )}
                      {/* 每次來店繳的到期日只是預估值，不判斷逾期 ——
                          畫面上要講出來，不然店家會以為系統漏抓 */}
                      {plan.frequency === 'per_visit' && (
                        <> · 不列逾期</>
                      )}
                    </div>
                  </div>
                  <div style={{ minWidth: 160 }}>
                    <Progress
                      percent={percent}
                      size='small'
                      strokeColor='#d5728a'
                    />
                  </div>
                </div>

                <div className='amounts'>
                  <span>
                    分期總額 <strong>{money(total)}</strong>
                    {plan.feeAmount > 0 && (
                      <span style={{ fontSize: 12, color: '#a0a0a5' }}>
                        （含手續費 {money(plan.feeAmount)}）
                      </span>
                    )}
                  </span>
                  <span>
                    已收 <strong>{money(plan.paidAmount)}</strong>
                  </span>
                  <span>
                    未收 <strong>{money(plan.outstanding)}</strong>
                  </span>
                  {plan.nextDueDate && (
                    <span>
                      下期 <strong>{plan.nextDueDate}</strong>（
                      {money(plan.nextDueAmount)}）
                    </span>
                  )}
                </div>

                <div className='actions'>
                  {canManage && plan.status === 'active' && (
                    <Button type='primary' onClick={() => setPayModalPlan(plan)}>
                      登錄收款
                    </Button>
                  )}
                  <Button onClick={() => setDetailPlan(plan)}>明細</Button>
                  {canManage && plan.status === 'active' && (
                    <Button danger type='text' onClick={() => handleCancelPlan(plan)}>
                      取消計畫
                    </Button>
                  )}
                </div>
              </PlanCard>
            )
          })
        )}
      </Spin>

      {pagination.total > pagination.pageSize && (
        <div style={{ textAlign: 'right', marginTop: 16 }}>
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            showSizeChanger
            onChange={(page, size) => fetchPlans(page, size)}
          />
        </div>
      )}

      {/* 登錄收款 */}
      <InstallmentPaymentModal
        open={!!payModalPlan}
        plan={payModalPlan}
        businessId={businessId}
        onClose={() => setPayModalPlan(null)}
        onPaid={() => {
          setPayModalPlan(null)
          fetchPlans(pagination.current, pagination.pageSize)
        }}
      />

      {/* 明細 */}
      <InstallmentDetailModal
        open={!!detailPlan}
        plan={detailPlan}
        businessId={businessId}
        canManage={canManage}
        onClose={() => setDetailPlan(null)}
        onChanged={() => fetchPlans(pagination.current, pagination.pageSize)}
      />
    </PageWrapper>
  )
}

export default InstallmentsPage
